import 'isomorphic-fetch';

import { getDynamicUrl } from '../middleware/lokaliseUrlMiddleware';
import { redirect } from '../history';

export const REQUEST_TOKEN = 'REQUEST_TOKEN';
export const REFRESH_TOKEN = 'REFRESH_TOKEN';
export const RESET_TOKEN = 'RESET_TOKEN';
export const TOKEN_RESPONSE = 'TOKEN_RESPONSE';

const TOKEN_EXPIRATION_SAFE_DELAY_SECONDS = 15;
let refreshInFlight = null;

function isTokenExpired(expiresAtSeconds, safeDelaySeconds = TOKEN_EXPIRATION_SAFE_DELAY_SECONDS) {
    if (!expiresAtSeconds) {
        return true;
    }
    const expiresAtMs = (Number(expiresAtSeconds) - safeDelaySeconds) * 1000;
    if (!Number.isFinite(expiresAtMs)) {
        return true;
    }
    return Date.now() >= expiresAtMs;
}

function hasValidToken(auth) {
    return Boolean(auth?.accessToken) && !isTokenExpired(auth?.expiresAt);
}

export function fetchToken() {
    return fetch(`${getDynamicUrl(process.env.URL_BFF)}/authentication/refresh`, {
        method: 'GET',
        headers: {
            'x-frontend-client': 'true'
        },
        credentials: 'include'
    }).then((response) => {
        if (response.status >= 200 && response.status < 300) {
            return response;
        }
        const error = new Error(response.statusText);
        error.statusCode = response.status;
        throw error;
    });
}

function computeClockSkew(response) {
    if (response instanceof Response && response.headers.get('Date')) {
        return new Date(response.headers.get('Date')).getTime() - new Date().getTime();
    } else if (response instanceof XMLHttpRequest) {
        return new Date(response.getResponseHeader('Date')).getTime() - new Date().getTime();
    } else {
        return 0;
    }
}

export function refreshToken() {
    return (dispatch, getState) => {
        // If another refresh already succeeded (or the store is already up to date),
        // don't trigger a new refresh call.
        if (hasValidToken(getState().auth)) {
            return Promise.resolve(null);
        }

        if (refreshInFlight) {
            return refreshInFlight;
        }

        refreshInFlight = fetchToken()
            .then((response) => handleTokenResponse(response))
            .then((action) => {
                dispatch(action);
                return action;
            })
            .catch((err) => {
                // A refresh can fail if another one happened at the same time (rotating refresh tokens),
                // but the session may still be valid now. If we already have a valid token in store,
                // ignore this failure to avoid logging out a user unnecessarily.
                if (hasValidToken(getState().auth)) {
                    return null;
                }

                dispatch(resetToken());
                if (err.statusCode === 401) {
                    redirect('/auth/logout');
                } else if (err.statusCode >= 402 && err.statusCode < 500) {
                    document.location.reload();
                } else {
                    redirect('/auth/logout');
                }
                throw err;
            })
            .finally(() => {
                refreshInFlight = null;
            });

        return refreshInFlight;
    };
}

export function handleTokenResponse(tokenResponse) {
    return (
        tokenResponse instanceof Response ? tokenResponse.json() : Promise.resolve(tokenResponse.response)
    ).then((json) => ({
        type: TOKEN_RESPONSE,
        payload: typeof json === 'string' ? JSON.parse(json) : json,
        clockCorrection: computeClockSkew(tokenResponse)
    }));
}

export function resetToken() {
    return {
        type: RESET_TOKEN
    };
}

export const handleUnauthorized = async (dispatch, func) => {
    try {
        return await func();
    } catch (err) {
        if (err.code === 401) {
            return dispatch(refreshToken()).then(func);
        } else {
            throw err;
        }
    }
};

export function handleRequestTokenFailure(request) {
    return (dispatch) => {
        if (request.status == 401) {
            console.info('Access token expired. Try to refresh it.');
            return dispatch(fetchToken());
        }
        return dispatch(resetToken());
    };
}
